import React from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {DateObservable} from "../../../stores/observables/dateObservable";
import {trimSeconds} from "../../../utils/dateUtils";
import {utcToZonedTime, zonedTimeToUtc} from "date-fns-tz";
import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import EditField from "../../molecules/editField/editField";
import ReactDatePicker from "react-datepicker";
import classNames from "classnames";
import {format} from "date-fns";
import * as Popper from "@popperjs/core";

export interface ITimePickerProps {
  interval?: number;
  timePickerErrorMessage?: string;
  date: DateObservable;
  timeZone: string;
  isDisabled?: boolean;
  placeholder?: string;
  disableMinTime?: boolean;
  minTime: Date;
  position?: Popper.Placement;
}
const TimePicker: React.FC<ITimePickerProps> = ({
  interval = 15,
  timePickerErrorMessage = "",
  date,
  timeZone,
  isDisabled = false,
  placeholder = "HH:MM",
  disableMinTime,
  minTime,
  position
}) => {
  const dateFormat = "hh:mm a";
  const {t} = useTranslation("translation");

  const getDefaultErrorMessage = () => {
    if (date.minDate && date.date && date.date < date.minDate) {
      return t("translation:errors.timePicker", {
        min: format(date.minDate, dateFormat),
        max: format(
          date.maxDate || new Date().setHours(23, 59, 0, 0),
          dateFormat
        )
      });
    } else if (date.maxDate && date.date && date.date > date.maxDate) {
      return t("translation:errors.timePicker", {
        min: format(
          date.minDate || new Date().setHours(0, 0, 0, 0),
          dateFormat
        ),
        max: format(date.maxDate, dateFormat)
      });
    }
  };
  return (
    <EditField
      validationMessage={timePickerErrorMessage || getDefaultErrorMessage()}
      showValidationMessage={!date.isValid}
      isRequired={true}
    >
      <ReactDatePicker
        className={classNames("time-picker form-control", {
          "is-invalid": !date.isValid,
          "is-disabled": isDisabled
        })}
        selected={date.date && utcToZonedTime(date.date, timeZone)}
        showTimeSelectOnly={true}
        placeholderText={placeholder}
        minTime={minTime}
        maxTime={setHours(
          setMinutes(zonedTimeToUtc(new Date(), timeZone), 59),
          23
        )}
        onChange={(val: Date) => {
          if (val) {
            date.setDate(trimSeconds(zonedTimeToUtc(val, timeZone)));
            // reset selected time to minimum valid time if required
            if (date.minDate && !disableMinTime && minTime > val) {
              date.setDate(trimSeconds(zonedTimeToUtc(minTime, timeZone)));
            }
          }
        }}
        dateFormat={dateFormat}
        showTimeSelect={true}
        timeIntervals={interval}
        showPopperArrow={false}
        disabled={isDisabled}
        popperPlacement={position}
      />
    </EditField>
  );
};

export default observer(TimePicker);
